import React, { memo } from "react";
import classes from "./Fields.scss";
import classNames from "classnames";
import Unveil from "UIComponents/Unveil";
import { colors, fontStyle } from "Constants";
import { getElementValue } from "modules/ElementValueUtils";
import { getPlannerFieldOptions } from "UnitPlans/modules/UnitPlanModule";
import FieldComponent from "UnitPlans/components/IBcomponents/FieldComponent";
import LabelComponent from "UnitPlans/components/LabelComponent";
import { getPropertyValue, getEmptyFieldValueStatus } from "Fields/FieldUtils";
import _ from "lodash";
import { connect } from "react-redux";

const inputStyle = {
  labelStyle: {
    ...fontStyle.demiBold,
    fontSize: "1.4rem",
    lineHeight: "20px",
    color: colors.neutral200,
  },
  textStyle: {
    ...fontStyle.medium,
    fontSize: "1.4rem",
    lineHeight: "20px",
    color: colors.neutral200,
  },
};
const nodeLabelStyle = [
  {
    depth: 0,
    style: {
      ...inputStyle.labelStyle,
    },
  },
  {
    depth: 1,
    style: {
      ...inputStyle.labelStyle,
    },
  },
  {
    depth: 2,
    style: {
      ...inputStyle.textStyle,
    },
  },
  {
    depth: 3,
    style: {
      ...inputStyle.textStyle,
    },
  },
];

//function to return custom style according to the viewType component from the template
const getFieldCustomStyles = ({ viewType }) => {
  switch (viewType) {
    case "Nestedview":
    case "ScopeAndSequence":
    case "NESTED_CHECKLIST":
      return {
        nodeLabelStyle,
      };
    case "RichText":
      return {
        applyDefaultClass: false,
        customTextStyle: inputStyle.textStyle,
      };
    case "ListView":
    case "InputNumberList":
      return {
        contentStyle: inputStyle.textStyle,
      };
    case "TextView":
      return {
        textViewcustomTextStyle: inputStyle.textStyle,
        isApplyCustomTextStyle: true,
      };
    case "GroupedConceptsView":
      return {
        headerLabelStyle: inputStyle.labelStyle,
        conceptLabelStyle: inputStyle.textStyle,
      };
    case "MultiSelectWithImage":
    case "IBMultiSelectQuestion":
      return {
        customTextStyle: inputStyle.textStyle,
        customLabelStyle: inputStyle.labelStyle,
      };
    case "IBRelatedConcepts":
      return {
        headerLabelStyle: inputStyle.labelStyle,
        listHeaderStyle: inputStyle.labelStyle,
        listItemsStyle: inputStyle.textStyle,
      };
    default:
      return {
        textStyle: inputStyle.textStyle,
        customTextStyle: inputStyle.textStyle,
      };
  }
};

const Fields = memo(function Fields(props) {
  const {
    t,
    fields,
    field_list,
    fieldListV2,
    unitPlanFields,
    organizationId,
    unitPlanDetails,
    selectedAcademicYear,
    currentCurriculumProgramType,
    isUnitFlowRevamp,
  } = props;

  return _.map(fields, fieldKey => {
    return _.has(fieldListV2, fieldKey) ? (
      <WrappedFieldComponentV2
        key={fieldKey}
        unitPlanFields={unitPlanFields}
        fieldKey={fieldKey}
        fieldListV2={fieldListV2}
        unitPlanDetails={unitPlanDetails}
        isUnitFlowRevamp={isUnitFlowRevamp}
      />
    ) : (
      <WrappedFieldComponent
        key={fieldKey}
        unitPlanFields={unitPlanFields}
        fieldKey={fieldKey}
        t={t}
        field_list={field_list}
        organizationId={organizationId}
        selectedAcademicYear={selectedAcademicYear}
        unitPlanDetails={unitPlanDetails}
        isUnitFlowRevamp={isUnitFlowRevamp}
        //only added curriculumProgramType to V1 since we only require this for
        //subjects and subjectLevel and they are only present in V1 currently
        currentCurriculumProgramType={currentCurriculumProgramType}
      />
    );
  });
});

const WrappedFieldComponent = props => {
  const {
    unitPlanFields,
    fieldKey,
    t,
    field_list,
    organizationId,
    selectedAcademicYear,
    unitPlanDetails,
    currentCurriculumProgramType,
    isUnitFlowRevamp,
  } = props;

  const field = field_list[fieldKey] ?? {};
  const showInUnitSummary = _.get(field, "config.showInUnitSummary", true);
  //skipping fields that have the below flag as false
  if (!showInUnitSummary) return null;
  let { viewType } = field;
  const { type, config: { viewLabel, label, styles, options } = {} } = field;

  //clean props
  const fieldLabel = viewLabel || label;

  viewType = viewType ?? type;

  // Providing Styling
  inputStyle.templateStyle = {
    ..._.get(styles, "style", {}),
    ..._.get(styles, "viewStyle", {}),
  };

  //extraction IDs from the Unit Data
  const unitPlanFieldValue = _.get(
    _.find(unitPlanFields, { uid: fieldKey }),
    "value",
    ""
  );

  //field data
  const resolvedValue =
    _.get(
      _.find(unitPlanFields, { uid: fieldKey }),
      "resolvedMinimalTree",
      ""
    ) || {};
  const subjectIds = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "value",
    ""
  );

  const subjectGroup = _.first(
    _.get(
      _.find(unitPlanFields, { uid: "subjectGroup" }),
      "resolvedMinimalTree.subjectGroups",
      []
    )
  );

  let value;

  //adding the subjects check since for dp we need to show subjects and subjectLevel together
  if (_.isEqual(fieldKey, "subjects")) {
    const subjectValue = getElementValue({
      fieldUID: fieldKey, // field key
      valueKeys: unitPlanFieldValue, // value key
      resolvedValue: resolvedValue, // value's resolved value
      isUnitPlan: true,
      subjectIds: subjectIds, // subjects IDs
      fieldObj: field,
      t,
      unitPlanFields,
    });
    if (_.isEqual(currentCurriculumProgramType, "IB_DP")) {
      const subjectLevelValue = getElementValue({
        fieldUID: "subjectLevel", // field key
        valueKeys: _.get(
          _.find(unitPlanFields, { uid: "subjectLevel" }),
          "value",
          ""
        ), // value key
        resolvedValue:
          _.get(
            _.find(unitPlanFields, { uid: "subjectLevel" }),
            "resolvedMinimalTree",
            ""
          ) || {}, // value's resolved value
        isUnitPlan: true,
        subjectIds: subjectIds, // subjects IDs
        fieldObj: field_list["subjectLevel"] ?? {},
        shouldStripRichTextStyles: true,
        t,
        unitPlanFields,
      });
      value = {
        subjectValue,
        subjectLevelValue,
        length: 1,
      };
    } else {
      value = subjectValue;
    }
  } else if (_.isEqual(fieldKey, "dateDuration")) {
    const { startDate, endDate } = unitPlanDetails;
    value = { startDate, endDate, length: 1 };
  } else
    value = getElementValue({
      fieldUID: fieldKey, // field key
      valueKeys: unitPlanFieldValue, // value key
      resolvedValue: resolvedValue, // value's resolved value
      isUnitPlan: true,
      subjectIds: subjectIds, // subjects IDs
      fieldObj: field,
      t,
      unitPlanFields,
    });

  const customStyles = getFieldCustomStyles({ viewType });

  //Displaying Label & value pairs using FieldComponent
  if (!_.isEmpty(value)) {
    const collapsible = _.get(field, "config.collapsible", true);

    const fieldContainerClasses = classNames({
      [classes.fieldContainer]: true,
      [classes.fieldContainerRevamp]: isUnitFlowRevamp,
    });

    return (
      <div className={fieldContainerClasses}>
        <Unveil
          maxHeight={collapsible ? 240 : "auto"}
          showCollapse={collapsible}
        >
          <div style={inputStyle.templateStyle}>
            <FieldComponent
              defaultOptions={getPlannerFieldOptions({
                fieldKey: fieldKey,
                defaultOptions: options,
                field_list,
                unitPlanFields,
                organizationId,
                t,
                selectedAcademicYear,
              })}
              type={viewType}
              mode={"view"}
              labelComponent={
                <LabelComponent
                  label={fieldLabel}
                  labelStyle={{
                    ...inputStyle.labelStyle,
                    marginBottom: "8px",
                  }}
                />
              }
              value={value}
              fieldKey={fieldKey}
              subjectGroup={subjectGroup}
              nodes={resolvedValue.nodes}
              resolvedValue={resolvedValue}
              isApplyTextViewcustomTextStyle={true}
              isListView={field?.config?.isListView}
              filterEmptyValues={field?.config?.filterEmptyValues}
              {...customStyles}
            />
          </div>
        </Unveil>
      </div>
    );
  } else {
    return null;
  }
};

const WrappedFieldComponentV2 = props => {
  const {
    unitPlanFields,
    fieldKey,
    fieldListV2,
    unitPlanDetails,
    isUnitFlowRevamp,
  } = props;

  const fieldObj = fieldListV2[fieldKey];
  const uiType = getPropertyValue({ propertyPath: "uiType", fieldObj });
  const label = getPropertyValue({ propertyPath: "label", fieldObj });
  const customStyles = getFieldCustomStyles({ viewType: uiType });

  //extraction IDs from the Unit Data
  const unitPlanFieldValue = _.get(
    _.find(unitPlanFields, { uid: fieldKey }),
    "value",
    ""
  );
  const unitPlanFieldObj = _.find(unitPlanFields, { uid: fieldKey });

  const isValueEmpty = getEmptyFieldValueStatus({
    fieldObj,
    fieldKey,
    data: unitPlanDetails,
  });

  if (isValueEmpty) {
    return null;
  }

  const fieldContainerClasses = classNames({
    [classes.fieldContainer]: true,
    [classes.fieldContainerRevamp]: isUnitFlowRevamp,
  });

  return (
    <div className={fieldContainerClasses}>
      <Unveil maxHeight={240} showCollapse={true}>
        <div style={inputStyle.templateStyle}>
          <FieldComponent
            value={unitPlanFieldValue}
            fieldKey={fieldKey}
            mode="view"
            type={`${uiType}_Ported`}
            emptyText="-"
            labelComponent={
              <LabelComponent
                label={label}
                labelStyle={{
                  ...inputStyle.labelStyle,
                  marginBottom: "8px",
                }}
              />
            }
            defaultOptions={JSON.stringify({})}
            fieldObj={unitPlanFieldObj}
            {...customStyles}
          />
        </div>
      </Unveil>
    </div>
  );
};

const mapStateToProps = state => {
  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    ""
  );
  return {
    currentCurriculumProgramType,
  };
};

export default connect(mapStateToProps)(Fields);
